// Sesion LTI emitida por el servicio LTI (Node + ltijs) tras validar el
// handshake con Moodle. El frontend recibe el JWT via querystring (?session=...)
// la primera vez que se carga el iframe, lo guarda en sessionStorage (NO
// localStorage: queremos que se descarte al cerrar la pestaña) y lo usa como
// Bearer en cada llamada al backend de LangGraph y al propio servicio LTI.

const SESSION_TOKEN_KEY = 'lti_session_token';
const SESSION_QUERY_PARAM = 'session';

// Evento global emitido cuando el wrapper HTTP detecta 401. SessionGuard se
// suscribe para mostrar el mensaje "vuelve al curso de Moodle" sin tener que
// refrescar la pagina.
export const SESSION_EXPIRED_EVENT = 'lti:session-expired';

// Captura el token de la URL, lo guarda en sessionStorage y limpia la URL
// para que el JWT no quede visible en el historial ni en compartir-link.
// Debe llamarse lo antes posible en el bootstrap (antes del primer render).
export function initializeLTISession() {
    try {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get(SESSION_QUERY_PARAM);
        if (!tokenFromUrl) return;

        window.sessionStorage.setItem(SESSION_TOKEN_KEY, tokenFromUrl);

        params.delete(SESSION_QUERY_PARAM);
        const remaining = params.toString();
        const cleanUrl = window.location.pathname
            + (remaining ? `?${remaining}` : '')
            + window.location.hash;
        window.history.replaceState({}, '', cleanUrl);
    } catch (_) {
        // sessionStorage bloqueado o entorno sin window: degradar silenciosamente.
    }
}

export function getSessionToken() {
    try {
        return window.sessionStorage.getItem(SESSION_TOKEN_KEY);
    } catch (_) {
        return null;
    }
}

export function clearSessionToken() {
    try {
        window.sessionStorage.removeItem(SESSION_TOKEN_KEY);
    } catch (_) {}
}

// JWTs usan base64URL (RFC 7515): reemplaza `+`→`-`, `/`→`_` y omite el
// padding `=`. atob solo entiende base64 estandar, asi que normalizamos antes
// de decodificar — si no, payloads que contienen `?` u otros bytes que caen en
// `_`/`-` rompen el decode silenciosamente y el guard rechaza tokens validos.
function base64UrlDecode(input) {
    let s = input.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    return atob(s);
}

// Validacion local del JWT: estructura (3 partes) y exp en el payload.
// No verifica firma — eso es responsabilidad del backend con SESSION_SECRET.
// Aqui solo evitamos llamar a la API con un token claramente invalido.
export function isValidSessionToken() {
    const token = getSessionToken();
    if (!token) return false;

    const parts = token.split('.');
    if (parts.length !== 3) return false;

    try {
        const payload = JSON.parse(base64UrlDecode(parts[1]));
        if (payload.exp && payload.exp * 1000 < Date.now()) return false;
        return true;
    } catch (_) {
        return false;
    }
}

// Extrae el email del payload del JWT (claim `userEmail`, igual que el backend).
// Devuelve '' si no hay token, si el payload no es decodificable o si el claim
// no existe. No valida la firma — solo lee el claim para reutilizarlo en el UI.
export function getSessionEmail() {
    const token = getSessionToken();
    if (!token) return '';
    const parts = token.split('.');
    if (parts.length !== 3) return '';
    try {
        const payload = JSON.parse(base64UrlDecode(parts[1]));
        const email = (payload.userEmail || payload.email || '').toString().trim();
        return email;
    } catch (_) {
        return '';
    }
}

export function notifySessionExpired() {
    try {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    } catch (_) {}
}
